<template>
  <div class="lp-select">
    <span
      v-if="!inlineLabel && (labelTop || labelTopSlotExist)"
      class="lp-input-label font-weight-medium"
    >
      <slot name="label-top" :props="props">
        <span>{{ labelTop }}</span>
        <span v-if="!preview && required" class="text-blue">*</span>
      </slot>
    </span>
    <span v-if="preview">
      {{ wrappedValue.name }}
    </span>
    <div
      v-else
      class="lp-select-inner"
      :class="[
        message
          ? `lp-input-wrapper-danger`
          : status && `lp-input-wrapper-${status}`,
        disabled && `lp-input-wrapper-disabled`,
      ]"
      :style="compact || message || helper ? '' : 'margin-bottom: 22px;'"
    >
      <div
        ref="trigger"
        v-clickoutside="(e: MouseEvent) => isClickOutside(e)"
        role="listbox"
        :aria-label="wrappedValue.name ?? wrappedValue"
        :aria-activedescendant="`${id}--option-${indexFocusedOption}`"
        :tabindex="disabled ? -1 : 0"
        class="lp-select-selection form-faux-select ly-row"
        :class="selectionClasses"
        v-bind="$attrs"
        @click="toggleDropdown"
        @keydown.tab="() => setOpen(false)"
        @keydown.down.stop.prevent="handleKey(EDirections.DOWN)"
        @keydown.up.stop.prevent="handleKey(EDirections.UP)"
        @keydown.esc.stop.prevent="() => setOpen(false)"
        @keydown.enter.stop.prevent="handleEnterKey"
      >
        <span
          v-if="
            (wrappedValue[trackBy] ||
              typeof wrappedValue[trackBy] === 'boolean' ||
              wrappedValue[trackBy] === 0) &&
            !triggerOnly
          "
          class="lp-select-selected"
        >
          <slot name="selected-option" :props="props">
            <span v-if="inlineLabel">{{ labelTop }}:</span>
            <span :class="`font-weight-${fontStyle}`">
              {{ wrappedValue.name ?? wrappedValue }}
            </span>
          </slot>
        </span>
        <span v-else-if="!triggerOnly" class="lp-select-placeholder">
          <slot name="placeholder" :props="props">
            {{ placeholder }}
          </slot>
        </span>

        <div class="lp-select-actions ly-row">
          <!--          <span v-if="clearable" class="xmr-2">-->
          <!--            <lp-icon name="times" @click="wrappedValue = null" />-->
          <!--          </span>-->
          <slot name="icon" :props="props">
            <lp-icon
              name="chevron-down"
              aria-hidden="true"
              :class="show && 'lp-select-arrowactive'"
            />
          </slot>
        </div>
      </div>
      <transition name="drop-bottom">
        <div v-if="show" class="lp-select-dropdown" :class="dropdownClasses">
          <div
            :ref="(dropdown) => setSelectListRef(dropdown)"
            class="lp-select-list menu-pop-up"
          >
            <div v-if="search" class="lp-select-search-wrapper">
              <lp-input
                v-model="searchValueRef"
                :placeholder="searchPlaceholder"
                prefix-icon="search"
                prefix-icon-aria-hidden="true"
                compact
                @keyup="handleSearchInput"
              >
                <template #clear>
                  <div v-show="searchValueRef" class="clearable">
                    <slot name="clear" :on-clear="clearSearch">
                      <lp-icon
                        class="lp-input-icon-wrapper"
                        name="close"
                        role="button"
                        @click.stop.prevent="clearSearch()"
                      />
                    </slot>
                  </div>
                </template>
              </lp-input>
            </div>
            <li
              v-for="(option, optionIndex) in computedOptions"
              :id="`${id}--option-${optionIndex}`"
              :key="optionIndex"
              :ref="(el) => setOptionRef(el, optionIndex as number)"
              class="lp-select-option ly-col"
              role="option"
              tabindex="0"
              :aria-selected="
                indexFocusedOption === optionIndex ? 'true' : 'false'
              "
              :class="[
                indexFocusedOption === optionIndex && CLASS_SELECTED_OPTION,
                wrappedValue[trackBy] === getOptionValue(option) &&
                  'lp-select-option-selected',
                option.disabled && 'lp-select-option-disabled',
                option.divided && 'border-top border-gray-medium',
                option.loadingState &&
                  props.showLoadingCentered &&
                  'ly-spacearound-center',
              ]"
              data-ua-multiple-id="ua-select-option"
              :data-ua-value="getOptionName(option)"
              :aria-label="
                option.loadingState ? props.loadingLabel : getOptionName(option)
              "
              @click="isOptionDisabled(option, optionIndex as number)"
              @keydown.enter.stop.prevent="
                isOptionDisabled(option, optionIndex as number)
              "
            >
              <div class="ly-row ly-start-center">
                <span
                  v-if="typeof option[prefix] === 'string' && option[prefix]"
                  class="lp-select-option-prefix"
                  v-html="option[prefix]"
                />
                <component :is="option[prefix]" v-else />

                <span
                  class="ly-col ly-spacearound-start"
                  :class="[
                    searchFilter &&
                      !option.loadingState &&
                      !getOptionName(option)
                        .toString()
                        .toLowerCase()
                        .includes(searchValueRef) &&
                      'lp-select-option-hidden',
                    option.loadingState && 'lp-select-option--loading',
                    option.loadingState &&
                      props.showLoadingLabel &&
                      'lp-select-option--loading-with-text',
                  ]"
                >
                  <slot name="option" :props="props" :option="option">
                    <span class="lp-select-option-label">
                      {{ getOptionName(option) }}
                      <lp-loading
                        v-if="option.loadingState"
                        :aria-label="loadingLabel"
                        :stroke="loadingIconStroke"
                        :radius="loadingIconRadius"
                      />
                    </span>
                  </slot>
                </span>
                <span
                  v-if="typeof option[suffix] === 'string' && option[suffix]"
                  class="lp-select-option-suffix"
                  v-html="option[suffix]"
                />
                <component :is="option[suffix]" v-else />
              </div>
              <p v-if="option.helper" v-html="option.helper" />
            </li>
          </div>
        </div>
      </transition>
    </div>
    <transition name="fade-bottom">
      <div v-if="message || helper" class="ly-row">
        <span
          v-if="message"
          class="lp-input-message mt-half d-flex"
          :class="[
            message
              ? `lp-input-message-danger`
              : status && `lp-input-message-${status}`,
            disabled && `lp-input-message-disabled`,
          ]"
        >
          {{ message }}
        </span>
        <span v-else class="lp-input-message mt-half text-gray">
          {{ helper }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import { Positions } from '@/models/enums'
import { clickOutside } from '@/directives'
import { useSelect } from '@/components/select/hooks'
import { TEmit } from '../../types'
import { TProps } from '@/types/global'
import {
  ALLOWED_POSITION,
  CLASS_SELECTED_OPTION,
} from '@/components/select/constants'
import { TOption, TSelectProps } from './types'
import { EDirections } from '@/models/enums'
import { useCheckSlot } from '@/hooks'

import uuid from '@/helpers/uuid'
import { t } from '@/plugins/i18n'

export default defineComponent({
  name: 'lp-select',
  computed: {
    t() {
      return t
    },
  },
  directives: {
    clickoutside: clickOutside,
  },
  inheritAttrs: false,
  props: {
    clearable: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    divided: { type: Boolean, default: false },
    fontStyle: { type: String, default: 'regular' },
    helper: { type: String, default: null },
    id: {
      type: String,
      default: uuid('select'),
    },
    inlineLabel: { type: Boolean, default: false },
    labelBy: { type: String, default: 'name' },
    labelTop: { type: String, default: null },
    loading: { type: Boolean, default: false },
    loadingIconRadius: { type: Number, default: 10 },
    loadingIconStroke: { type: Number, default: 2 },
    loadingLabel: { type: String, default: 'Loading' },
    message: { type: String, default: null },
    modelValue: { type: [String, Number, Boolean, Object], default: null },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select option' },
    placement: {
      type: String,
      default: Positions.B,
      validator: (value: Positions) => ALLOWED_POSITION.includes(value),
    },
    prefix: { type: String, default: 'prefix' },
    preview: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    search: { type: Boolean, default: false },
    searchFilter: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: t('Shared.FilterOptions') },
    searchValue: { type: String, default: '' },
    showLoadingCentered: { type: Boolean, default: true },
    showLoadingLabel: { type: Boolean, default: false },
    showNull: { type: Boolean, default: false },
    singleValue: { type: Boolean, default: false },
    status: { type: String, default: 'neutral' },
    suffix: { type: String, default: 'suffix' },
    trackBy: { type: String, default: 'value' },
    triggerOnly: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'update:model-value', 'update:searchValue'],
  setup(props: TProps, { emit, slots }) {
    const labelTopSlotExist = useCheckSlot(slots, 'label-top') !== null
    const modal = false
    const searchValue = ref(props.searchValue)

    const {
      wrappedValue,
      getOptionName,
      getOptionValue,
      setOptionRef,
      indexFocusedOption,
      optionsRefs,
      show,
      handleEnterKey,
      setOpen,
      setSelectListRef,
      toggleDropdown,
      selectOption,
      handleKey,
    } = useSelect(props as TSelectProps, emit as TEmit)

    const selectionClasses = computed(() => ({
      'lp-select-selection-disabled': props.disabled,
      'lp-select-selection-active': show.value,
      'lp-select-trigger-only': props.triggerOnly,
    }))

    const dropdownClasses = computed(() => ({
      [props.placement
        ? `lp-select-dropdown-${props.placement}`
        : `lp-select-dropdown-${Positions.B}`]: true,
      'lp-select-dropdown-divided': props.divided,
    }))

    const computedOptions = computed(() => {
      if (props.loading)
        return [
          {
            [props.labelBy]: props.showLoadingLabel ? props.loadingLabel : '',
            [props.trackBy]: null,
            disabled: true,
            loadingState: true,
          },
        ]

      return props.showNull
        ? [
            typeof props.options[0] !== 'object'
              ? null
              : { [props.labelBy]: props.placeholder, [props.trackBy]: null },
            ...props.options,
          ]
        : props.options
    })

    const clearSearch = () => {
      searchValue.value = ''
    }

    const isClickOutside = (e: MouseEvent) => {
      if (e.target) {
        const el = e.target as HTMLElement

        if (
          !el.classList.contains('lp-input') &&
          !el.classList.contains('lpi-close')
        ) {
          setOpen(false)
        }
      }
    }

    const isOptionDisabled = (option: TOption, optionIndex: number) => {
      option.disabled ? (show.value = false) : selectOption(optionIndex)
    }

    const handleSearchInput = () => {
      emit('update:searchValue', searchValue.value)
    }

    return {
      labelTopSlotExist,
      CLASS_SELECTED_OPTION,
      wrappedValue,
      getOptionName,
      getOptionValue,
      setOptionRef,
      indexFocusedOption,
      optionsRefs,
      show,
      handleEnterKey,
      setOpen,
      setSelectListRef,
      toggleDropdown,
      selectOption,
      handleKey,
      selectionClasses,
      dropdownClasses,
      props,
      EDirections,
      modal,
      computedOptions,
      searchValueRef: searchValue,
      clearSearch,
      isClickOutside,
      isOptionDisabled,
      handleSearchInput,
    }
  },
})
</script>
